import { useMemo } from 'react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';

import {
  useEvotorIntegrationSettings,
  useRetailEvotorAccounts,
  useRetailEvotorDevices,
  useRetailEvotorStores,
} from '../../hooks';
import { useDomainPermissions } from '../../lib';
import type { RetailTab } from '../../model/retailNav';
import { SettingsCard } from './SettingsCard';

/**
 * How many of the current stores list to sample when deriving the "bound" / "stock export on" /
 * "last exported" summary figures below. There is no aggregate/count-by-filter endpoint for these
 * (see `retailEvotorApi.ts`), so — mirroring `RetailEvotorStoresPanel`'s own location-picker
 * precedent (`useStoreLocations({ page: 0, size: 100 })`) — this reads a generously-sized single
 * page and treats it as representative. For a tenant with more stores than this, the figures
 * under-count; a true aggregate would need a new backend endpoint, out of scope here.
 */
const STORE_SAMPLE_SIZE = 100;

type StepVisualState = 'done' | 'todo' | 'loading' | 'error' | 'no-access';

interface JourneyStep {
  key: string;
  title: string;
  description: string;
  state: StepVisualState;
  targetTab: RetailTab;
}

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(
    date,
  );
}

function StepIcon({ state }: { state: StepVisualState }) {
  switch (state) {
    case 'done':
      return <CheckCircleRoundedIcon color="success" />;
    case 'loading':
      return <CircularProgress size={20} />;
    case 'error':
      return <ErrorOutlineRoundedIcon color="error" />;
    case 'no-access':
      return <LockOutlinedIcon color="disabled" />;
    default:
      return <RadioButtonUncheckedRoundedIcon color="disabled" />;
  }
}

export interface RetailEvotorOverviewPanelProps {
  onNavigateToTab: (tab: RetailTab) => void;
}

/**
 * Обзор tab — the default tab. A launch checklist over the merchant's actual journey (connect →
 * load → map → export → automate), each step derived from data this page's other tabs already
 * fetch (no new endpoints), plus a compact state summary. This is deliberately the *only* tab that
 * reads from every other domain's query at once — it is a dashboard over them, not a data owner.
 */
export function RetailEvotorOverviewPanel({ onNavigateToTab }: RetailEvotorOverviewPanelProps) {
  const { t } = useTranslation('settings');

  const accountsQuery = useRetailEvotorAccounts({ page: 0, size: 1 });
  const storesQuery = useRetailEvotorStores({
    page: 0,
    size: STORE_SAMPLE_SIZE,
    sort: 'updatedAt:desc',
  });
  const devicesQuery = useRetailEvotorDevices({ page: 0, size: 1 });

  const { canView: canViewAutomation } = useDomainPermissions('evotor-integration');
  const automationQuery = useEvotorIntegrationSettings({ enabled: canViewAutomation });

  const stores = useMemo(() => storesQuery.data?.content ?? [], [storesQuery.data]);
  const boundStoresCount = useMemo(() => stores.filter((s) => Boolean(s.locationId)).length, [stores]);
  const stockExportOnCount = useMemo(
    () => stores.filter((s) => s.stockExportEnabled).length,
    [stores],
  );
  const lastStockExportAt = useMemo(
    () =>
      stores.reduce<string | null>((latest, s) => {
        if (!s.lastStockExportAt) return latest;
        if (!latest) return s.lastStockExportAt;
        return new Date(s.lastStockExportAt) > new Date(latest) ? s.lastStockExportAt : latest;
      }, null),
    [stores],
  );

  const productExportStep = automationQuery.data?.steps.find((s) => s.key === 'PRODUCT_EXPORT');
  const productGroupExportStep = automationQuery.data?.steps.find(
    (s) => s.key === 'PRODUCT_GROUP_EXPORT',
  );

  function queryState(query: {
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
  }): 'loading' | 'error' | 'ready' {
    if (query.isLoading) return 'loading';
    if (query.isError) return 'error';
    return 'ready';
  }

  const accountsState = queryState(accountsQuery);
  const accountsDone = (accountsQuery.data?.page.totalElements ?? 0) > 0;

  const storesState = queryState(storesQuery);
  const storesLoadedDone = (storesQuery.data?.page.totalElements ?? 0) > 0;
  const storesBoundDone = boundStoresCount > 0;

  // The two Automation-derived steps (§4/§5) share this base state: unreachable without
  // `integrations.evotor.view`, otherwise whatever `automationQuery` is doing right now.
  const automationBaseState: Exclude<StepVisualState, 'done' | 'todo'> | 'ready' = !canViewAutomation
    ? 'no-access'
    : automationQuery.isLoading
      ? 'loading'
      : automationQuery.isError
        ? 'error'
        : 'ready';
  const catalogExportDone = Boolean(productExportStep?.enabled || productGroupExportStep?.enabled);
  const anyAutomationEnabled = Boolean(automationQuery.data?.steps.some((s) => s.enabled));

  const steps: JourneyStep[] = [
    {
      key: 'account',
      title: t('retailSettings.overview.steps.account.title'),
      description: t('retailSettings.overview.steps.account.description'),
      state: accountsState === 'ready' ? (accountsDone ? 'done' : 'todo') : accountsState,
      targetTab: 'accounts',
    },
    {
      key: 'storesLoaded',
      title: t('retailSettings.overview.steps.storesLoaded.title'),
      description: t('retailSettings.overview.steps.storesLoaded.description'),
      state: storesState === 'ready' ? (storesLoadedDone ? 'done' : 'todo') : storesState,
      targetTab: 'stores',
    },
    {
      key: 'storesBound',
      title: t('retailSettings.overview.steps.storesBound.title'),
      description: t('retailSettings.overview.steps.storesBound.description'),
      state: storesState === 'ready' ? (storesBoundDone ? 'done' : 'todo') : storesState,
      targetTab: 'stores',
    },
    {
      key: 'catalogExported',
      title: t('retailSettings.overview.steps.catalogExported.title'),
      description: t('retailSettings.overview.steps.catalogExported.description'),
      state: automationBaseState === 'ready' ? (catalogExportDone ? 'done' : 'todo') : automationBaseState,
      targetTab: 'sync',
    },
    {
      key: 'automationOn',
      title: t('retailSettings.overview.steps.automationOn.title'),
      description: t('retailSettings.overview.steps.automationOn.description'),
      state:
        automationBaseState === 'ready' ? (anyAutomationEnabled ? 'done' : 'todo') : automationBaseState,
      targetTab: 'automation',
    },
  ];

  const completedCount = steps.filter((step) => step.state === 'done').length;

  return (
    <Stack spacing={2.5}>
      <SettingsCard
        title={t('retailSettings.overview.checklistTitle')}
        description={t('retailSettings.overview.checklistDescription', {
          completed: completedCount,
          total: steps.length,
        })}
        divided={false}
      >
        <List disablePadding sx={{ width: '100%' }}>
          {steps.map((step) => {
            const disabledForAccess = step.state === 'no-access';
            const row = (
              <ListItemButton
                key={step.key}
                onClick={() => onNavigateToTab(step.targetTab)}
                disabled={disabledForAccess}
                sx={{ borderRadius: 2, py: 1.25 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <StepIcon state={step.state} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {step.title}
                    </Typography>
                  }
                  secondary={step.description}
                />
                {!disabledForAccess ? (
                  <ChevronRightRoundedIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
                ) : null}
              </ListItemButton>
            );
            return disabledForAccess ? (
              <Tooltip key={step.key} title={t('retailSettings.noPermissionTooltip')} placement="right">
                <Box>{row}</Box>
              </Tooltip>
            ) : (
              row
            );
          })}
        </List>
      </SettingsCard>

      <SettingsCard title={t('retailSettings.overview.summaryTitle')} divided={false}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            pt: 1,
          }}
        >
          <SummaryStat
            label={t('retailSettings.overview.summary.accounts')}
            value={accountsQuery.data?.page.totalElements}
            loading={accountsQuery.isLoading}
          />
          <SummaryStat
            label={t('retailSettings.overview.summary.stores')}
            value={storesQuery.data?.page.totalElements}
            loading={storesQuery.isLoading}
          />
          <SummaryStat
            label={t('retailSettings.overview.summary.devices')}
            value={devicesQuery.data?.page.totalElements}
            loading={devicesQuery.isLoading}
          />
          <SummaryStat
            label={t('retailSettings.overview.summary.storesBound')}
            value={storesQuery.isSuccess ? boundStoresCount : undefined}
            loading={storesQuery.isLoading}
          />
          <SummaryStat
            label={t('retailSettings.overview.summary.stockExportOn')}
            value={storesQuery.isSuccess ? stockExportOnCount : undefined}
            loading={storesQuery.isLoading}
          />
          <SummaryStat
            label={t('retailSettings.overview.summary.lastStockExportAt')}
            value={storesQuery.isSuccess ? (formatDateTime(lastStockExportAt) ?? t('retailSettings.overview.summary.never')) : undefined}
            loading={storesQuery.isLoading}
          />
        </Box>
      </SettingsCard>
    </Stack>
  );
}

interface SummaryStatProps {
  label: string;
  value: number | string | undefined;
  loading: boolean;
}

function SummaryStat({ label, value, loading }: SummaryStatProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      {loading ? (
        <CircularProgress size={16} sx={{ mt: 0.5 }} />
      ) : (
        <Typography variant="h6" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {value ?? '—'}
        </Typography>
      )}
    </Box>
  );
}
